import { collection, doc, getDoc, getDocs, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { normalizePhoneNumber } from "./spinnerOtpService";

export const USER_OFFERS_COLLECTION = "UserOffers";

const getUserOfferDocRef = ({ phoneNumber, serviceSlug }) => {
  const normalizedPhone = normalizePhoneNumber(phoneNumber);

  return {
    normalizedPhone,
    offerRef: doc(db, USER_OFFERS_COLLECTION, normalizedPhone, "services", serviceSlug),
  };
};

export const getSavedOfferForService = async ({ phoneNumber, serviceSlug }) => {
  const { normalizedPhone, offerRef } = getUserOfferDocRef({ phoneNumber, serviceSlug });
  if (normalizedPhone.length < 10) return null;

  const snap = await getDoc(offerRef);

  if (!snap.exists()) return null;

  return snap.data() ?? null;
};

export const saveUserOfferForService = async ({ phoneNumber, serviceSlug, offer }) => {
  const { normalizedPhone, offerRef } = getUserOfferDocRef({ phoneNumber, serviceSlug });

  if (normalizedPhone.length < 10) {
    throw new Error("Invalid phone number");
  }

  const existingSnap = await getDoc(offerRef);

  if (existingSnap.exists()) {
    throw new Error("Offer already exists for this service");
  }

  await setDoc(
    offerRef,
    {
      phoneNumber: normalizedPhone,
      serviceSlug,
      percentageOff: offer?.percentageOff ?? null,
      flatOff: offer?.flatOff ?? null,
      updatedAt: new Date().toISOString(),
    },
    { merge: false }
  );

  return normalizedPhone;
};

/**
 * Admin upsert: grant/overwrite an offer for a user + service, and clear any
 * prior redemption so the app surfaces it again (client filters on used != true).
 * Unlike saveUserOfferForService (public spinner, one-time), this overwrites.
 */
export const grantUserOfferForService = async ({
  phoneNumber,
  serviceSlug,
  offer,
}) => {
  const { normalizedPhone, offerRef } = getUserOfferDocRef({
    phoneNumber,
    serviceSlug,
  });

  if (normalizedPhone.length < 10) {
    throw new Error("Enter a valid 10-digit phone number.");
  }
  if (!serviceSlug) {
    throw new Error("Select a service.");
  }

  const hasPercentage = offer?.percentageOff != null;
  const hasFlat = offer?.flatOff != null;
  if (hasPercentage === hasFlat) {
    // exactly one of the two must be set
    throw new Error("Set either a percentage OR a flat discount, not both.");
  }

  await setDoc(
    offerRef,
    {
      phoneNumber: normalizedPhone,
      serviceSlug,
      percentageOff: offer?.percentageOff ?? null,
      flatOff: offer?.flatOff ?? null,
      // A freshly granted offer is unused and thus visible to the app again.
      used: false,
      updatedAt: new Date().toISOString(),
    },
    { merge: true }
  );

  return normalizedPhone;
};

/** List every service offer saved for a given phone number. */
export const getOffersForPhone = async (phoneNumber) => {
  const normalizedPhone = normalizePhoneNumber(phoneNumber);
  if (normalizedPhone.length < 10) return [];

  const servicesRef = collection(
    db,
    USER_OFFERS_COLLECTION,
    normalizedPhone,
    "services"
  );
  const snap = await getDocs(servicesRef);

  return snap.docs.map((d) => ({ serviceSlug: d.id, ...d.data() }));
};